"use client";
import { useEffect, useState } from "react";
import { DetailsViewProps } from "../../types/ui.types";
import { PALETTE } from "../../lib/colorPallete";
import { Loader2, Minus, Plus } from "lucide-react";
import { motion } from "framer-motion";

export const DetailsView: React.FC<DetailsViewProps> = ({
  product,
  cartItemToEdit,
  availabilityMap,
  selectedCustomizations,
  handleCustomizationSelect,
  hasAccessoriesCustomization,
  premiumPackageData,
  isPremiumEnabled,
  setIsPremiumEnabled,
  handleConfirm,
  handleBuyNow,
  isCartLoading,
  priceBreakdown,
  isWholesaleUser,
  finalDimensions,
  quantity,
  onQuantityChange,
  minQuantity,
  isSelectedColorOutOfStock,
}) => {
  const [quantityInput, setQuantityInput] = useState(quantity.toString());
  useEffect(() => {
    setQuantityInput(quantity.toString());
  }, [quantity]);

  const handleQuantityInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    setQuantityInput(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= minQuantity) {
      onQuantityChange(numValue);
    }
  };

  const handleQuantityInputBlur = () => {
    const numValue = parseInt(quantityInput);
    if (isNaN(numValue) || numValue < minQuantity) {
      setQuantityInput(minQuantity.toString());
      onQuantityChange(minQuantity);
    }
  };

  const formattedTotalPrice = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(priceBreakdown.total * quantity);

  const isProductOutOfStock = !product.inStock;
  const isVariantOutOfStock = isProductOutOfStock || isSelectedColorOutOfStock;

  return (
    <div className="w-full flex flex-col pl-4 pr-4 pt-4 sm:pl-6 sm:pr-6 sm:pt-6 h-full overflow-y-auto">
      <div className="grow">
        <div className="hidden md:block">
          <h3
            className="font-serif text-2xl sm:text-3xl font-bold"
            style={{ color: PALETTE.darkGreen }}
          >
            {product.modelName}
          </h3>
          <p
            className="mt-1 text-base leading-snug"
            style={{ color: `${PALETTE.darkGreen}B3` }}
          >
            {product.subtext}
          </p>

          {}
          {isProductOutOfStock && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-100 border border-red-300">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-red-700 font-semibold text-sm">
                Out of Stock
              </span>
            </div>
          )}
        </div>

        {}
        {product.customization && product.customization.length > 0 && (
          <div className="my-6">
            <h4
              className="font-semibold text-lg mb-4"
              style={{ color: PALETTE.darkGreen }}
            >
              Customize Your Product
            </h4>
            <div className="space-y-4">
              {product.customization
                .filter((cust) => cust.type !== "Accessories")
                .map((cust) => {
                  const availableOptions = cust.options.filter(
                    (opt) =>
                      availabilityMap[cust.type]?.[opt.value] &&
                      !(!isWholesaleUser && opt.isWholeSaleOnly) &&
                      !(isWholesaleUser && opt.isRetailOnly),
                  );
                  if (availableOptions.length === 0) return null;
                  return (
                    <div key={cust.type}>
                      <h5
                        className="font-medium mb-2 uppercase text-sm tracking-wide"
                        style={{ color: `${PALETTE.darkGreen}CC` }}
                      >
                        {cust.type}
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {availableOptions.map((opt) => {
                          const isSelected =
                            selectedCustomizations[cust.type] === opt.value;
                          return (
                            <button
                              key={opt.value}
                              onClick={() =>
                                handleCustomizationSelect(cust.type, opt.value)
                              }
                              disabled={isProductOutOfStock}
                              className="px-4 py-2 text-sm rounded-lg border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{
                                borderColor: isSelected
                                  ? PALETTE.gold
                                  : `${PALETTE.midGreen}40`,
                                backgroundColor: isSelected
                                  ? PALETTE.gold
                                  : "transparent",
                                color: isSelected
                                  ? PALETTE.darkerGreen
                                  : PALETTE.darkGreen,
                                fontWeight: isSelected ? 600 : 400,
                              }}
                            >
                              {opt.value.split(" (for")[0]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>

            {}
            {hasAccessoriesCustomization && premiumPackageData.isAvailable && (
              <div
                className="mt-6 p-4 rounded-lg border"
                style={{
                  backgroundColor: `${PALETTE.midGreen}10`,
                  borderColor: `${PALETTE.midGreen}40`,
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h5
                      className="font-medium text-sm uppercase tracking-wide"
                      style={{ color: PALETTE.darkGreen }}
                    >
                      Add Premium Accessories
                    </h5>
                    <p
                      className="text-xs mt-1"
                      style={{ color: `${PALETTE.darkGreen}99` }}
                    >
                      Standard is included. +₹
                      {premiumPackageData.cost.toLocaleString("en-IN")} for
                      premium {premiumPackageData.label}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={isPremiumEnabled}
                      onChange={(e) => setIsPremiumEnabled(e.target.checked)}
                      disabled={isProductOutOfStock}
                    />
                    <div
                      className="w-11 h-6 rounded-full peer transition-colors duration-200 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-disabled:opacity-50"
                      style={{
                        backgroundColor: isPremiumEnabled
                          ? PALETTE.gold
                          : `${PALETTE.midGreen}40`,
                      }}
                    ></div>
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        {}
        <div
          className="my-6 h-px w-full"
          style={{ backgroundColor: `${PALETTE.midGreen}40` }}
        />
        <h4
          className="font-semibold text-lg mb-4"
          style={{ color: PALETTE.darkGreen }}
        >
          Specifications
        </h4>
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div>
            <span
              className="font-semibold uppercase tracking-wider text-xs"
              style={{ color: `${PALETTE.darkGreen}99` }}
            >
              DIMENSIONS (L×W×H)
            </span>
            <p className="font-medium" style={{ color: PALETTE.darkGreen }}>
              {finalDimensions.length}cm × {finalDimensions.width}cm ×{" "}
              {finalDimensions.height}cm
            </p>
          </div>
          {product.description &&
            Object.entries(product.description).map(([key, value]) => (
              <div key={key}>
                <span
                  className="font-semibold uppercase tracking-wider text-xs"
                  style={{ color: `${PALETTE.darkGreen}99` }}
                >
                  {key.replace(/_/g, " ")}:
                </span>
                <p className="font-medium" style={{ color: PALETTE.darkGreen }}>
                  {value as string}
                </p>
              </div>
            ))}
        </div>
      </div>

      {}
      <div
        className="mt-6 pt-4 border-t flex flex-col gap-4 sticky bottom-0 pb-4"
        style={{
          borderColor: `${PALETTE.midGreen}40`,
          backgroundColor: PALETTE.cream,
        }}
      >
        {}
        {isWholesaleUser && !isVariantOutOfStock && (
          <div
            className="text-sm space-y-1"
            style={{ color: PALETTE.darkGreen }}
          >
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                }).format(priceBreakdown.subtotal * quantity)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Tax ({product.wholeSaleTax}%)</span>
              <span>
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                }).format(priceBreakdown.tax * quantity)}
              </span>
            </div>
          </div>
        )}

        {}
        <div className="flex items-center justify-between gap-4">
          {!isVariantOutOfStock ? (
            <div className="flex items-center gap-3">
              <button
                onClick={() => onQuantityChange(quantity - 1)}
                disabled={isCartLoading || quantity <= minQuantity}
                className="p-2 rounded-full border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  borderColor: `${PALETTE.midGreen}40`,
                  color: PALETTE.darkGreen,
                }}
              >
                <Minus size={16} />
              </button>

              <input
                type="number"
                min={minQuantity}
                value={quantityInput}
                onChange={handleQuantityInputChange}
                onBlur={handleQuantityInputBlur}
                disabled={isCartLoading}
                className="w-16 h-10 text-center font-bold text-lg rounded-md border transition-colors disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                style={{
                  borderColor: `${PALETTE.midGreen}40`,
                  color: PALETTE.darkerGreen,
                  backgroundColor: PALETTE.cream,
                }}
              />

              <button
                onClick={() => onQuantityChange(quantity + 1)}
                disabled={isCartLoading}
                className="p-2 rounded-full border transition-colors disabled:opacity-50"
                style={{
                  borderColor: `${PALETTE.midGreen}40`,
                  color: PALETTE.darkGreen,
                }}
              >
                <Plus size={16} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="font-semibold text-red-700">
                {isSelectedColorOutOfStock
                  ? "Unavailable for selection"
                  : "Out of Stock"}
              </span>
            </div>
          )}

          {!isVariantOutOfStock && (
            <motion.p
              className="font-serif text-2xl sm:text-3xl font-semibold text-right"
              style={{ color: PALETTE.darkGreen }}
              key={formattedTotalPrice}
              initial={{ opacity: 0.5, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {formattedTotalPrice}
            </motion.p>
          )}
        </div>

        {}
        {minQuantity > 1 && !isVariantOutOfStock && (
          <div className="text-center text-xs font-semibold text-amber-700 bg-amber-100 p-2 rounded-md">
            Minimum {isWholesaleUser ? 'wholesale' : 'order'} quantity: {minQuantity} units
          </div>
        )}

        {}
        <div className="grid grid-cols-2 gap-4">
          {isVariantOutOfStock ? (
            <div className="col-span-2 flex items-center justify-center py-3 px-4 rounded-full bg-gray-100 border-2 border-gray-300">
              <span className="font-semibold text-gray-600 uppercase tracking-wider text-sm">
                {isSelectedColorOutOfStock
                  ? "Currently Out of Stock"
                  : "Currently Out of Stock"}
              </span>
            </div>
          ) : (
            <>
              <button
                onClick={handleConfirm}
                disabled={isCartLoading || quantity < minQuantity}
                className="w-full px-4 py-3 rounded-full font-semibold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: PALETTE.gold,
                  color: PALETTE.darkerGreen,
                }}
              >
                {isCartLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    {cartItemToEdit ? "Updating..." : "Adding..."}
                  </>
                ) : cartItemToEdit ? (
                  "Update Item"
                ) : (
                  "Add to Cart"
                )}
              </button>

              <button
                onClick={handleBuyNow}
                disabled={isCartLoading || quantity < minQuantity}
                className="w-full px-4 py-3 rounded-full font-semibold uppercase tracking-wider text-sm transition-all duration-300 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: PALETTE.darkerGreen,
                  color: PALETTE.gold,
                }}
              >
                Buy Now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
